/* The chart strip — the measurable answers of one client's form, side by side.
 *
 * Promoted with `SurveyChartsStrip.tsx` out of `pages/client-detail` (4 November 2026), when the
 * review inbox on `/raporty` became its second reader. The grid and the occurrence cells are
 * `shared/ui/chart/chartRow` and `shared/ui/occurrenceCells`, shared with the client's Trendy tab;
 * the card and its head below are the strip's own, and the rest moved here from
 * `SurveysTab.module.css`.
 *
 * Every value is a token. */

/* ── the card ──────────────────────────────────────────────────── */

.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-14px);
  min-width: 0;
  padding: var(--space-15px) var(--space-4);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}


.cardHead {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

/* The question the card charts, in sentence case (owner, 4 November 2026): the tracked uppercase
 * eyebrow it wore shouted the trainer's own question. It wraps rather than ellipsising — a card
 * whose head is cut off has stopped saying what it measures. */
.chartName {
  align-self: stretch;
  font-size: var(--type-control);
  font-style: normal;
  font-weight: var(--fw-semibold);
  line-height: var(--type-control-lh);
  color: var(--text);
  text-wrap: pretty;
}

.chartFigure {
  flex: none;
  font-size: var(--fs-session);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-section);
  font-variant-numeric: tabular-nums;
}

/* The change over the window: neutral ink and its arrow (owner, 4 November 2026). A weight going
 * down is not good news for every client, and the strip does not know whose goal it is. */
.chartDelta {
  display: inline-flex;
  flex: none;
  gap: var(--space-1);
  align-items: center;
  font-size: var(--fs-micro);
  font-style: normal;
  font-weight: var(--fw-regular);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ── the chart strip ────────────────────────────────────────────────────────── */

/* Four or more measurable questions do not fit three equal columns, and stacking them would turn a
 * comparison into a scroll of the page. So they scroll SIDEWAYS in the form's own question order,
 * one card snapping to the start of the port at a time — fit or scroll, never clip.
 *
 * `overflow: auto hidden`: the vertical axis must stay `hidden` or the port grows a second bar for
 * a card that is exactly as tall as it is. */
.chartScroller {
  --sb-size: var(--space-2);
  --sb-radius: var(--radius-hair);

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: var(--w-survey-chart-card);
  gap: var(--space-5);
  overflow: auto hidden;
  scroll-snap-type: x mandatory;
}

.chartCard {
  scroll-snap-align: start;
}

/* The ONE card the row draws when the read failed or the form asks nothing measurable. It spans the
 * whole three-column grid, so the row is the same width in all four states — without this it stood
 * in the first track and read as a chart that had lost its two neighbours. */
.chartSpan {
  grid-column: 1 / -1;
}

/* The card head, over TWO lines: the question, then its reading and change under it.
 *
 * `DetailTab`'s `.chartHead` is one line with the figure beside the name, which is right for Trendy
 * — „TONAŻ" is a word. A trainer's own question is a sentence, and ellipsising it („CZY WYKONAŁEŚ
 * WSZYS…") leaves the card saying nothing about what it measures. So this head is a column, and its
 * box is RESERVED at the two-line height whether or not the second line is used: cards stand side
 * by side in one row and a head that grew when its data landed would move every plot beside it. */
.chartHead {
  flex-direction: column;
  gap: var(--space-1);
  align-items: flex-start;
  min-height: var(--h-survey-chart-head);
}

/* The reading and its change, on their own line under the question.
 *
 * `margin-block-start: auto` pushes it to the BOTTOM of the head's reserve, so every figure in the
 * row stands on one line whether its question took one line or two. Without it a two-line question
 * dropped its figure 13px below its neighbours' and the row read as ragged. Baseline-aligned inside
 * itself, so the change sits on the figure's own line rather than on the middle of it. */
.chartReading {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  margin-block-start: auto;
}

/* ── the two-option card ────────────────────────────────────────────────────── */

/* The ring and the occurrences it is a share OF, side by side inside the plot's own reserve — so a
 * choice card is exactly as tall as the line cards beside it and the row has one baseline. */
.binary {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  block-size: var(--chart-card-reserve);
}

.binaryStrip {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

/* The SECOND option. Not the attendance grid's „missed" cell, which wears `--danger`: a two-option
 * question is not necessarily yes/no — „zielony"/„niebieski" is just as valid — so neither option
 * may be painted as the bad one. An OUTLINE against the first option's fill, in the neutral edge
 * this app already draws every quiet border in. */
.optCellB {
  border: var(--border-hairline) solid var(--border-2);
}

/* Which drawing is which answer, and how many of each. A fill beside an outline says nothing until
 * something names them, and this card may not say it with colour. */
.binaryLegend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-3);
  align-items: center;
  padding: var(--space-0);
  margin: var(--space-0);
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  list-style: none;
}

.binaryLegendItem {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: center;
  min-width: 0;
}

.binaryCount {
  font-weight: var(--fw-bold);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* The legend's own swatch: the cell drawing at a legend's size. */
.binarySwatch {
  flex: none;
  width: var(--sz-survey-swatch);
}

/* The window's two ends. The dates BETWEEN them are on the stream to the left, so printing one per
 * cell would be ten labels saying what one list already says. */
.binaryDates {
  display: flex;
  justify-content: space-between;
  margin: var(--space-0);
  font-size: var(--fs-tag);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}
